import { create } from 'zustand'
import { api } from '../lib/api/client'

interface InteractionRecord {
  elementId: string
  type: string
  timestamp: number
  position?: [number, number]
}

interface BookState {
  // Reader state
  currentBookId: string
  currentPage: number
  totalPages: number
  flipProgress: number
  isFlipping: boolean
  direction: 'next' | 'prev' | null
  interactionHistory: InteractionRecord[]
  lightingPreset: string
  particlePreset: string | null
  isPlaying: boolean
  currentWordIndex: number
  ttsTimestamps: { word: string; start: number; end: number }[]

  // API state
  books: any[]
  currentBook: any | null
  bookLoading: boolean
  bookError: string | null

  setCurrentPage: (page: number) => void
  startFlip: (direction: 'next' | 'prev') => void
  updateFlipProgress: (progress: number) => void
  endFlip: () => void
  recordInteraction: (record: InteractionRecord) => void
  setLightingPreset: (preset: string) => void
  setParticlePreset: (preset: string | null) => void
  setIsPlaying: (playing: boolean) => void
  setCurrentWordIndex: (index: number) => void
  setTTSTimestamps: (timestamps: { word: string; start: number; end: number }[]) => void

  // API actions
  fetchBooks: () => Promise<void>
  fetchBook: (slug: string) => Promise<void>
  setCurrentBookId: (slug: string | null) => void
}

export const useBookStore = create<BookState>((set, get) => ({
  currentBookId: 'otto-shy-moon',
  currentPage: 0,
  totalPages: 10,
  flipProgress: 0,
  isFlipping: false,
  direction: null,
  interactionHistory: [],
  lightingPreset: 'moonlight',
  particlePreset: 'fireflies',
  isPlaying: false,
  currentWordIndex: -1,
  ttsTimestamps: [],

  // API state
  books: [],
  currentBook: null,
  bookLoading: false,
  bookError: null,

  setCurrentPage: (page) => {
    set({ currentPage: page })
    // P7-02: 持久化阅读进度到 localStorage
    const state = get()
    if (state.currentBook?.id) {
      saveReadingProgress(state.currentBook.id, page, state.totalPages)
    }
  },
  startFlip: (direction) => set({ isFlipping: true, direction, flipProgress: 0 }),
  updateFlipProgress: (progress) => set({ flipProgress: progress }),
  endFlip: () => set({ isFlipping: false, direction: null, flipProgress: 0 }),
  recordInteraction: (record) => 
    set((state) => ({ 
      interactionHistory: [...state.interactionHistory, record].slice(-10)
    })),
  setLightingPreset: (preset) => set({ lightingPreset: preset }),
  setParticlePreset: (preset) => set({ particlePreset: preset }),
  setIsPlaying: (playing) => set({ isPlaying: playing }),
  setCurrentWordIndex: (index) => set({ currentWordIndex: index }),
  setTTSTimestamps: (timestamps) => set({ ttsTimestamps: timestamps }),

  // API actions
  fetchBooks: async () => {
    set({ bookLoading: true, bookError: null });
    try {
      const data = await api.books.list();
      set({ books: data.books || [], bookLoading: false });
    } catch (err: any) {
      set({ bookError: err.message, bookLoading: false });
    }
  },
  fetchBook: async (slug) => {
    set({ bookLoading: true, bookError: null });
    try {
      const data = await api.books.get(slug);
      // P7-02: 恢复阅读进度
      const restoredPage = loadReadingProgress(data.id || slug)
      set({ 
        currentBook: data, 
        currentPage: restoredPage ?? 0,
        totalPages: data.pages?.length || 1,
        bookLoading: false 
      });
    } catch (err: any) {
      set({ bookError: err.message, bookLoading: false });
    }
  },
  setCurrentBookId: (slug) => {
    if (!slug) {
      set({ currentBook: null });
    }
  },
}))

// P7-02: 阅读进度持久化工具
const PROGRESS_KEY = '3dthreebook_reading_progress'

function saveReadingProgress(bookId: string, currentPage: number, totalPages: number) {
  try {
    const raw = localStorage.getItem(PROGRESS_KEY)
    const all: Record<string, { page: number; total: number; updatedAt: string }> = raw ? JSON.parse(raw) : {}
    all[bookId] = {
      page: currentPage,
      total: totalPages,
      updatedAt: new Date().toISOString(),
    }
    localStorage.setItem(PROGRESS_KEY, JSON.stringify(all))
    // 异步同步到 API（静默失败）
    syncProgressToAPI(bookId, currentPage, totalPages)
  } catch {
    // localStorage 不可用（隐私模式）时静默失败
  }
}

function loadReadingProgress(bookId: string): number | null {
  try {
    const raw = localStorage.getItem(PROGRESS_KEY)
    if (!raw) return null
    const all = JSON.parse(raw)
    const record = all[bookId]
    if (!record) return null
    // 检查是否过期（30 天）
    const updated = new Date(record.updatedAt)
    const daysOld = (Date.now() - updated.getTime()) / (1000 * 60 * 60 * 24)
    if (daysOld > 30) {
      delete all[bookId]
      localStorage.setItem(PROGRESS_KEY, JSON.stringify(all))
      return null
    }
    return record.page ?? null
  } catch {
    return null
  }
}

async function syncProgressToAPI(bookId: string, currentPage: number, totalPages: number) {
  const token = localStorage.getItem('token')
  if (!token) return
  try {
    await api.progress.update(bookId, {
      current_page: currentPage,
      total_pages: totalPages,
    })
  } catch {
    // 静默失败，离线时等待下次同步
  }
}

// P7-02: 全局进度恢复 hook（在应用启动时调用）
export function initReadingProgress() {
  try {
    const raw = localStorage.getItem(PROGRESS_KEY)
    if (!raw) return
    const all = JSON.parse(raw)
    const entries = Object.entries(all) as [string, { page: number; total: number; updatedAt: string }][]
    // 清理超过 30 天的记录
    const now = Date.now()
    let changed = false
    for (const [bookId, record] of entries) {
      const updated = new Date(record.updatedAt)
      const daysOld = (now - updated.getTime()) / (1000 * 60 * 60 * 24)
      if (daysOld > 30) {
        delete all[bookId]
        changed = true
      }
    }
    if (changed) {
      localStorage.setItem(PROGRESS_KEY, JSON.stringify(all))
    }
  } catch {
    // 静默失败
  }
}

interface UserState {
  currentUser: string | null
  language: string
  fontSize: number
  volume: number
  
  setLanguage: (lang: string) => void
  setFontSize: (size: number) => void
  setVolume: (vol: number) => void
}

export const useUserStore = create<UserState>((set) => ({
  currentUser: null,
  language: 'zh',
  fontSize: 24,
  volume: 0.8,
  
  setLanguage: (lang) => set({ language: lang }),
  setFontSize: (size) => set({ fontSize: size }),
  setVolume: (vol) => set({ volume: vol }),
}))
